import { useEffect, useState } from 'react';
import {
  AsProp,
  ButtonSecondary,
  DropdownLink,
  DropdownList,
  DropdownTrigger,
  IconChevronDown,
  Type,
} from '@spotify-internal/encore-web';

export interface DropdownItem {
  text: string;
  id: string;
}

interface DropdownProps {
  items: DropdownItem[];
  id: string;
  onChange: (index: string) => void;
  iconLeading?: AsProp;
  defaultId?: string;
}

export const Dropdown = ({
  items,
  id,
  onChange,
  defaultId,
  iconLeading,
}: DropdownProps) => {
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(
    defaultId ? items.findIndex(item => item.id === defaultId) : 0,
  );

  const dropdownListId = `${id}-dropdown-list`;
  const triggerId = `${id}-trigger`;

  useEffect(() => {
    if (show) {
      const element = document.getElementById(dropdownListId) as HTMLElement;
      element.focus();
    }
  }, [show, dropdownListId]);

  const onClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    setShow(!show);
    setSelected(index);
    onChange(items[index].id);
  };

  const onHide = () => {
    setShow(false);
    const element = document.getElementById(triggerId) as HTMLElement;
    element.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const upArrow = 'ArrowUp';
    const downArrow = 'ArrowDown';
    const enter = 'Enter';
    const escape = 'Escape';
    const tab = 'Tab';
    const space = ' ';

    switch (e.key) {
      case upArrow: {
        e.preventDefault();
        if (selected !== 0) {
          onChange(items[selected - 1].id);
          setSelected(selected - 1);
        }
        break;
      }

      case downArrow: {
        e.preventDefault();
        if (selected < items.length - 1) {
          onChange(items[selected + 1].id);
          setSelected(selected + 1);
        }
        break;
      }

      case enter:
      case escape:
      case space:
      case tab: {
        e.preventDefault();
        setShow(false);
        const element = document.getElementById(triggerId) as HTMLElement;
        element.focus();
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <>
      <DropdownTrigger
        overlay={
          show && (
            <DropdownList
              onKeyDown={e => onKeyDown(e)}
              onBlur={() => setShow(false)}
              id={dropdownListId}
              data-testid={dropdownListId}
              aria-activedescendant={items[selected].id}
            >
              {items.map((item, index) => (
                <DropdownLink
                  as="li"
                  role="option"
                  id={item.id}
                  key={item.id}
                  onFocus={() => setSelected(index)}
                  onClick={e => onClick(e, index)}
                  selected={items[selected].id === item.id}
                  aria-selected={items[selected].id === item.id}
                >
                  {item.text}
                </DropdownLink>
              ))}
            </DropdownList>
          )
        }
        onShow={() => setShow(true)}
        onHide={onHide}
        data-testid={triggerId}
      >
        <ButtonSecondary
          id={triggerId}
          aria-labelledby={`dropdown-label ${triggerId}`}
          aria-expanded={show || undefined}
          size="small"
          iconLeading={iconLeading}
          iconTrailing={IconChevronDown}
        >
          <Type variant="finaleBold">{items[selected].text}</Type>
        </ButtonSecondary>
      </DropdownTrigger>
    </>
  );
};
